/**
 * Collection Tabs JS
 */

class CollectionTabs extends HTMLElement {
  static INITIALIZATION_DELAY = 10;
  static FLICKITY_INIT_DELAY = 50;
  static TAB_TRANSITION_DELAY = 300;
  
  static FLICKITY_OPTIONS = {
    cellAlign: 'left',
    contain: true,
    pageDots: false,
    prevNextButtons: true,
    freeScroll: false,
    wrapAround: false,
    adaptiveHeight: false,
    groupCells: true
  };

  constructor() {
    super();
    
    // Add a class to indicate loading state
    this.classList.add('is-initializing');
    
    // Initialize properties
    this.tabLinks = this.querySelectorAll('.tab-link');
    this.tabContents = this.querySelectorAll('.tab-content');
    this.tabsContainer = this.querySelector('.tabs');
    this.tabsSlider = this.querySelector('.tabs-slider');
    this.tabsArrows = {
      prev: this.querySelector('[data-tabs-arrow-prev]'),
      next: this.querySelector('[data-tabs-arrow-next]')
    };
    this.viewAllCta = this.querySelector('[data-view-all-cta]');
    this.viewAllWrapper = this.querySelector('[data-view-all-wrapper]');
    
    // Use a small delay to ensure the DOM is fully ready
    setTimeout(() => {
      // Ensure proper DOM structure before initialization
      this._ensureProperStructure();
      
      // Initialize components in the correct order
      this._initializeComponents();
    }, 10);
  }
  
  /**
   * Ensure proper DOM structure to prevent flickering
   */
  _ensureProperStructure() {
    // Check if we need to create a container for tab contents
    let tabContentsContainer = this.querySelector('.tab-contents-container');
    
    // If no container exists, we need to create one and organize the tab contents
    if (!tabContentsContainer) {
      const container = document.createElement('div');
      container.className = 'tab-contents-container';
      
      // Find all tab contents that are direct children of collection-tabs
      // This handles the case where they're rendered directly in the HTML
      const collectionTabs = this.querySelector('.collection-tabs');
      if (collectionTabs) {
        // Get all direct tab content children
        const directTabContents = collectionTabs.querySelectorAll(':scope > .tab-content');
        
        if (directTabContents.length > 0) {
          // Move these direct tab contents into the container
          directTabContents.forEach(content => {
            content.parentNode.removeChild(content);
            container.appendChild(content);
          });
          
          // Insert the container after the tab__scroll element
          const tabScroll = this.querySelector('.tab__scroll');
          if (tabScroll) {
            tabScroll.parentNode.insertBefore(container, tabScroll.nextSibling);
          } else {
            // Fallback: append to collection-tabs
            collectionTabs.appendChild(container);
          }
          
          // Update the tabContents reference
          this.tabContents = container.querySelectorAll('.tab-content');
        } else {
          // Handle the case where tab contents are not direct children
          // This is the original code path
          if (this.tabContents.length > 0) {
            // Move all tab contents into this container
            this.tabContents.forEach(content => {
              content.parentNode.removeChild(content);
              container.appendChild(content);
            });
            
            // Add the container after the tab__scroll element
            const tabScroll = this.querySelector('.tab__scroll');
            if (tabScroll && tabScroll.parentNode) {
              tabScroll.parentNode.insertBefore(container, tabScroll.nextSibling);
            } else {
              // Fallback: Add to the collection-tabs element
              collectionTabs.appendChild(container);
            }
            
            // Update the tabContents reference
            this.tabContents = container.querySelectorAll('.tab-content');
          }
        }
      }
    }
    
    // Make sure all tab contents are properly positioned initially
    this.tabContents.forEach((content, index) => {
      if (index === 0) {
        // First tab is visible but with opacity 0 until fully initialized
        content.classList.add('current');
        content.style.opacity = '0';
      } else {
        // Other tabs are hidden
        content.classList.remove('current');
      }
    });
    
    // Make sure first tab is marked as current
    if (this.tabLinks.length > 0) {
      this.tabLinks[0].classList.add('current');
    }

    this.updateViewAllCta(this.querySelector('.tab-link.current'));
  }
  
  /**
   * Initialize all components in the correct order
   */
  _initializeComponents() {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Ensure the tab contents container exists and is properly set up
      const tabContentsContainer = this.querySelector('.tab-contents-container');
      if (tabContentsContainer) {
        // Update the tabContents reference in case the DOM structure has changed
        this.tabContents = tabContentsContainer.querySelectorAll('.tab-content');
      }
      
      // Initialize tabs first
      this.initTabs();
      
      // Initialize arrows
      this.initArrows();
      
      // Initialize Flickity with a slight delay to ensure DOM is ready
      setTimeout(() => {
        this.initFlickity();
        
        // Check arrow visibility
        this.checkArrowsVisibility();
        
        // Remove loading state and show content
        this.classList.remove('is-initializing');
        
        // Fade in the first tab content
        const firstContent = this.tabContents[0];
        if (firstContent) {
          firstContent.style.opacity = '1';
        }
      }, 50);
      
      // Also check on window resize
      window.addEventListener('resize', () => {
        this.checkArrowsVisibility();
      });
    });
  }

  initTabs() {
    this.tabLinks.forEach(tabLink => {
      tabLink.addEventListener('click', this.handleTabClick.bind(this));
    });
  }

  updateViewAllCta(activeTab) {
    if (!this.viewAllCta || !this.viewAllWrapper) return;

    const collectionUrl = activeTab?.dataset.collectionUrl || '';

    if (collectionUrl) {
      this.viewAllCta.setAttribute('href', collectionUrl);
      this.viewAllCta.removeAttribute('aria-hidden');
      this.viewAllCta.removeAttribute('tabindex');
      this.viewAllWrapper.classList.remove('is-hidden');
      return;
    }

    this.viewAllCta.removeAttribute('href');
    this.viewAllCta.setAttribute('aria-hidden', 'true');
    this.viewAllCta.setAttribute('tabindex', '-1');
    this.viewAllWrapper.classList.add('is-hidden');
  }

  handleTabClick(event) {
    // Prevent default behavior
    event.preventDefault();
    
    const clickedTab = event.currentTarget;
    const tabIndex = clickedTab.getAttribute('data-tab');
    
    // Don't do anything if this tab is already active
    if (clickedTab.classList.contains('current')) {
      return;
    }
    
    // Find the active content before removing classes
    const activeContent = this.querySelector(`.tab-content-${tabIndex}`);
    if (!activeContent) return;
    
    // Get the currently active tab and content
    const currentTab = this.querySelector('.tab-link.current');
    const currentContent = this.querySelector('.tab-content.current');
    
    // Add a class to indicate we're changing tabs
    this.classList.add('is-changing-tab');
    
    // Batch DOM updates to prevent layout thrashing
    // Use requestAnimationFrame to ensure changes happen in the next paint cycle
    requestAnimationFrame(() => {
      // First, mark the new tab as current
      clickedTab.classList.add('current');
      
      // Then prepare the new content but keep it hidden
      // This ensures it's ready to be displayed without flickering
      activeContent.style.opacity = '0';
      activeContent.classList.add('current');
      
      // Force a reflow to ensure the browser processes the changes
      void activeContent.offsetWidth;
      
      // Now remove current class from previous tab and content
      if (currentTab && currentTab !== clickedTab) {
        currentTab.classList.remove('current');
      }
      
      if (currentContent && currentContent !== activeContent) {
        currentContent.classList.remove('current');
        
        // Ensure the previous content is properly hidden
        // This is important for the absolute positioning to work correctly
        currentContent.style.visibility = 'hidden';
        currentContent.style.opacity = '0';
      }
      
      // Smooth scroll the clicked tab into view with animation
      this.scrollTabIntoView(clickedTab);

      this.updateViewAllCta(clickedTab);
      
      // Finally, fade in the new content
      activeContent.style.visibility = 'visible';
      activeContent.style.opacity = '1';
      
      // Reinitialize Flickity for the current tab with a delay
      // to ensure the DOM has fully updated
      setTimeout(() => {
        const flickityElement = activeContent.querySelector('[data-carousel]');
        if (flickityElement) {
          if (typeof Flickity !== 'undefined' && flickityElement.flickity) {
            flickityElement.flickity.resize();
          } else if (customElements.get('flickity-carousel')) {
            // Force the custom element to update
            const flickityCarousel = activeContent.querySelector('flickity-carousel');
            if (flickityCarousel) {
              flickityCarousel.update && flickityCarousel.update();
            }
          }
        }
        
        // Remove the changing tab class
        this.classList.remove('is-changing-tab');
      }, 300); // Longer delay to ensure transitions complete
    });
  }

  scrollTabIntoView(tab) {
    if (!this.tabsSlider || !tab) return;
    
    const tabRect = tab.getBoundingClientRect();
    const sliderRect = this.tabsSlider.getBoundingClientRect();
    
    // Calculate the center position
    const tabCenter = tabRect.left + tabRect.width / 2;
    const sliderCenter = sliderRect.left + sliderRect.width / 2;
    
    // Calculate the scroll amount to center the tab
    const scrollAmount = tabCenter - sliderCenter;
    
    // Smooth scroll with animation
    this.tabsSlider.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  }

  initArrows() {
    if (!this.tabsSlider) return;
    
    // Check if arrows should be visible based on scrollability
    this.checkArrowsVisibility();
    
    // Listen for scroll events to update arrow visibility
    if (this.tabsSlider) {
      this.tabsSlider.addEventListener('scroll', this.checkArrowsVisibility.bind(this));
    }
    
    // Arrow navigation for tabs
    if (this.tabsArrows.prev) {
      this.tabsArrows.prev.addEventListener('click', () => {
        const scrollAmount = this.tabsSlider.clientWidth / 2;
        this.tabsSlider.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      });
    }
    
    if (this.tabsArrows.next) {
      this.tabsArrows.next.addEventListener('click', () => {
        const scrollAmount = this.tabsSlider.clientWidth / 2;
        this.tabsSlider.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      });
    }
  }
  
  // Check scroll position and update mask gradient
  checkArrowsVisibility() {
    if (!this.tabsSlider) return;
    
    const tabsSliderContainer = this.querySelector('.tabs-slider');
    if (!tabsSliderContainer) return;
  }

  initFlickity() {
    const carousels = this.querySelectorAll('[data-carousel]');
    
    carousels.forEach(carousel => {
      const options = {
        cellAlign: 'left',
        contain: true,
        pageDots: false,
        prevNextButtons: true,
        freeScroll: false,
        wrapAround: false,
        adaptiveHeight: false,
        groupCells: true,
        accessibility: true
      };
      
      // Initialize Flickity
      if (typeof Flickity !== 'undefined') {
        const flickityInstance = new Flickity(carousel, options);
        
        // Add event listeners to prevent clicks passing through disabled buttons
        if (flickityInstance.prevButton && flickityInstance.prevButton.element) {
          flickityInstance.prevButton.element.addEventListener('click', (e) => {
            if (flickityInstance.prevButton.element.disabled) {
              e.preventDefault();
              e.stopPropagation();
            }
          });
        }
        
        if (flickityInstance.nextButton && flickityInstance.nextButton.element) {
          flickityInstance.nextButton.element.addEventListener('click', (e) => {
            if (flickityInstance.nextButton.element.disabled) {
              e.preventDefault();
              e.stopPropagation();
            }
          });
        }
      } else {
        // If Flickity is not loaded yet, use the custom element
        carousel.setAttribute('data-flickity', JSON.stringify(options));
        
        // Add a listener for when the carousel is initialized
        carousel.addEventListener('ready.flickity', (event) => {
          const flickityInstance = event.detail.flickity;
          
          // Add event listeners to prevent clicks passing through disabled buttons
          if (flickityInstance.prevButton && flickityInstance.prevButton.element) {
            flickityInstance.prevButton.element.addEventListener('click', (e) => {
              if (flickityInstance.prevButton.element.disabled) {
                e.preventDefault();
                e.stopPropagation();
              }
            });
          }
          
          if (flickityInstance.nextButton && flickityInstance.nextButton.element) {
            flickityInstance.nextButton.element.addEventListener('click', (e) => {
              if (flickityInstance.nextButton.element.disabled) {
                e.preventDefault();
                e.stopPropagation();
              }
            });
          }
        });
      }
    });
  }
}

// Register the custom element
customElements.define('collection-tabs', CollectionTabs);

// Initialize tabs when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const collectionTabsSection = document.querySelector('[data-section-type="collection-tabs"]');
  if (collectionTabsSection) {
    new CollectionTabs(collectionTabsSection);
  }
});

// Initialize Keen Slider for tabs navigation
document.addEventListener('DOMContentLoaded', function() {
  const tabsSliderElement = document.getElementById('tabs-navigation-slider');
  if (!tabsSliderElement) return;

  let tabsSlider = null;

  function initializeSlider() {
    if (window.innerWidth < 1024) {
      if (!tabsSlider) {
        tabsSlider = new KeenSlider("#tabs-navigation-slider", {
          loop: false,
          mode: "snap",
          rtl: false,
          slides: { 
            perView: "auto",
            spacing: 15,
            origin: "auto"
          }
        });
      }
    } else if (tabsSlider) {
      tabsSlider.destroy();
      tabsSlider = null;
    }
  }

  // Initialize on load
  initializeSlider();

  // Re-initialize on resize
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(initializeSlider, 100);
  });
});
